import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNotifications } from "@/context/NotificationsContext";
import { Bell, BellOff, CheckCheck, CalendarClock, ClipboardList, Users, MessageSquare, ArrowRight } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ar as arLocale } from "date-fns/locale";

const FILTERS = ["all", "unread"];

function iconFor(type = "") {
  if (type.startsWith("booking")) return CalendarClock;
  if (type.startsWith("task")) return ClipboardList;
  if (type.startsWith("event") || type.startsWith("rsvp")) return Users;
  if (type.startsWith("message") || type.startsWith("ticket")) return MessageSquare;
  return Bell;
}

export function DashboardNotificationsTab() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { items, unreadCount, markRead, markAllRead } = useNotifications();
  const [filter, setFilter] = useState("all");
  const isAr = i18n.language === "ar";
  const locale = isAr ? arLocale : undefined;

  const visible = useMemo(
    () => (filter === "unread" ? items.filter((i) => !i.is_read) : items),
    [items, filter],
  );

  const openOne = async (n) => {
    if (!n.is_read) await markRead(n.notification_id);
    if (n.link) {
      if (n.link.startsWith("http")) window.open(n.link, "_blank", "noopener");
      else navigate(n.link);
    }
  };

  const rel = (isoStr) => {
    try { return formatDistanceToNow(parseISO(isoStr), { addSuffix: true, locale }); } catch { return ""; }
  };

  return (
    <div className="space-y-4" data-testid="dashboard-notifications">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display text-base md:text-lg font-semibold flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" />
          {t("memberNotifications.title", { defaultValue: "Notification centre" })}
        </h3>
        {unreadCount > 0 && (
          <Badge className="rounded-full bg-rose-500/12 text-rose-600 border-rose-500/30" data-testid="notifications-unread-badge">
            {unreadCount} {t("memberNotifications.unread", { defaultValue: "unread" })}
          </Badge>
        )}
        <div className="ms-auto flex items-center gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-all ${
                filter === f ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-primary"
              }`}
              data-testid={`notifications-filter-${f}`}
            >
              {t(`memberNotifications.filter.${f}`, { defaultValue: f })}
            </button>
          ))}
          <Button
            size="sm"
            variant="outline"
            className="rounded-xl gap-1.5 text-xs"
            onClick={markAllRead}
            disabled={unreadCount === 0}
            data-testid="notifications-mark-all"
          >
            <CheckCheck className="h-3.5 w-3.5" />
            {t("memberNotifications.markAll", { defaultValue: "Mark all read" })}
          </Button>
        </div>
      </div>

      {visible.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-12 text-center space-y-2" data-testid="notifications-empty">
          <BellOff className="h-6 w-6 text-muted-foreground mx-auto" />
          <p className="text-sm text-muted-foreground">
            {t("memberNotifications.empty", { defaultValue: "Nothing here yet — approvals, session requests and replies will land here." })}
          </p>
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="notifications-list">
          {visible.map((n) => {
            const Icon = iconFor(n.type || "");
            return (
              <li key={n.notification_id}>
                <button
                  type="button"
                  onClick={() => openOne(n)}
                  className={`w-full text-start flex items-start gap-3 rounded-2xl border p-3.5 transition-all hover:shadow-sm ${
                    n.is_read ? "border-border bg-card" : "border-primary/30 bg-primary/5"
                  }`}
                  data-testid={`notification-${n.notification_id}`}
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${n.is_read ? "bg-muted/50 text-muted-foreground" : "bg-primary/12 text-primary"}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm ${n.is_read ? "" : "font-semibold"}`}>{(isAr && n.content_ar) || n.content}</span>
                    <span className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="font-mono uppercase tracking-widest">
                        {t(`notificationTypes.${n.type}`, { defaultValue: (n.type || "").replace(/_/g, " ") })}
                      </span>
                      <span>·</span>
                      <span>{rel(n.created_at)}</span>
                    </span>
                  </span>
                  {!n.is_read && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
                  {n.link && <ArrowRight className="h-4 w-4 text-muted-foreground mt-1.5 rtl-flip shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default DashboardNotificationsTab;
